import {JsonNode} from "@api/ApiModels";
import {BREAKPOINT} from "@lib/Cms/CmsModels";
import {ProductCardProps} from "@components/Product/ProductCard/ProductCard";

export enum CatalogRedemptionModalityType {
    SELL = "SELL",
    SELF = "SELF",
    POINTS = "POINTS",
}

export enum CatalogPricingModelType {
    ONE_SHOT = "ONE_SHOT",
    RECURRING = "RECURRING",
}

export enum CatalogProductType {
    MULTI = "MULTI-ITEM",
    SINGLE = "SINGLE",
    BUNDLE = "BUNDLE",
}

export enum CatalogProductSpecificType {
    ITEM = "ITEM",
    VOUCHER = "VOUCHER",
    BENEFIT = "BENEFIT",
    SERVICE = "SERVICE",
}

export interface CatalogProduct {
    logicId: string;
    catalogProductLogicId?: string;
    externalId: string;
    beginTimestamp: string;
    endTimestamp: string;
    redemptionBeginTimestamp?: string;
    redemptionEndTimestamp?: string;
    availability: number | null;
    purchaseEnabled: boolean;
    title: string;
    longDescription: string;
    shortDescription: string;
    description?: string;
    termsAndConditions?: string;
    afterPurchaseInfo?: string;
    catalog: Catalog;
    supplier: CatalogSupplier;
    partner: CatalogPartner;
    brands: CatalogBrand[];
    sectors: CatalogSector[];
    mediaContents: CatalogProductMediaContent[];
    type: CatalogProductType;
    productType: CatalogProductSpecificType;
    relatedProducts: CatalogRelatedProduct[];
    redemptionModalities: CatalogRedemptionModality[];
    tags: CatalogTag[];
    attributes: CatalogProductAttribute[];
}

export interface CatalogPricingModel {
    customData?: JsonNode;
    cronDescription: string;
    type: CatalogPricingModelType;
}

export interface CatalogRedemptionModality {
    customData?: JsonNode;
    logicId: string;

    pointsPrice: number;
    discountPoint: number;

    selfPoints: number;
    discountSelfPoints: number;

    selfCash: number;
    discountSelfCash: number;

    sellPrice: number;
    discountSellPrice: number;

    finalPointsPrice: number;
    finalSelfPoints: number;
    finalSelfCash: number;
    finalSellPrice: number;

    walletConfigurationLogicId: string;
    type: CatalogRedemptionModalityType;

    pricingModel: CatalogPricingModel;
}

export interface Catalog {
    logicId: string;
    externalId: string;
    currency: string;
    catalogGroups: CatalogGroup[];
}

export interface CatalogGroup {
    logicId: string;
    externalId: string;
}

export interface CatalogSupplier {
    logicId: string;
    externalId: string;
}

export interface CatalogPartner {
    logicId: string;
    externalId: string;
}

export interface CatalogBrand {
    logicId?: string | null;
    externalId?: string | null;
    title: string;
    description?: string | null;
    brandLogo: string;
}

export interface CatalogSector {
    customData?: JsonNode;
    logicId: string;
    externalId: string;
    title: string;
}

export interface CatalogProductMediaContent {
    customData?: JsonNode;
    mediaType: CatalogMediaType;
    flDefault: boolean;
    mediaUrl: string;
    flPreview: boolean;
    imageBreakpoint?: BREAKPOINT | null;
}

export enum CatalogMediaType {
    IMAGE = "IMAGE",
    PDF = "PDF",
    EMBED_VIDEO = "EMBED_VIDEO",
    SELF_HOSTED_VIDEO = "SELF_HOSTED_VIDEO",
    CUSTOM = "CUSTOM",
}

export interface CatalogTag {
    customData?: JsonNode;
    position: string;
    title: string;
    value: string;
    imgUrl: string;
    colorCode: string;
}

export interface CatalogRelatedProduct {
    logicId: string;
    attributes: CatalogProductAttribute[];
}

export interface CatalogProductAttribute {
    logicId: string;
    externalId: string;
    title: string;
    values: string[];
}

export interface CatalogResponse {
    customData: {
        pageCount: number;
        recordsCount: number;
    };
    results: CatalogProduct[];
}

// Cart
export interface CartResponse {
    pointsTotal: number;
    walletConfigurationLogicId?: string;
    items: {
        catalogGroupProductLogicId: string;
        productType: CatalogProductSpecificType;
        quantity: number;
        statusCode?: string;
    }[];
}

export interface AddToCartRequest {
    productLogicId: string;
    redemptionModalityLogicId: string;
    quantity: number;
}

export interface RedemptionRequestAddress {
    email: string;

    phone?: string | null;
    name?: string | null;
    surname?: string | null;
    businessName?: string | null;
    careOf?: string | null;

    country?: string | null;
    province?: string | null;
    city?: string | null;
    postalCode?: string | null;
    street?: string | null;
    note?: string | null;
}

export interface ConfirmCartRequest {
    address: RedemptionRequestAddress;
}

// UI
export interface ProductCardVm extends ProductCardProps {
    productLogicId: string;
    purchasable: boolean;
    brand: string;
    brandLogo?: string | null;
    category: string;
}

export interface ProductVm extends ProductCardVm {
    redemptionModalityId?: string;
    productType: CatalogProductSpecificType;
    shortDescription?: string;
    longDescription?: string;
    terms?: string;
    brandDescription?: string;
}